import { omit } from 'lodash';
import { updatedEntities, denormalisedEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { parse } from '../../util/urlHelpers';
import { getTeamAssociatedListing, apiBaseUrl } from '../../util/api';
import { types as sdkTypes, transit } from '../../util/sdkLoader';
import {
  STORE_SIDEBAR_CATEGORIES,
  STORE_SIDEBAR_SUBCATEGORIES,
  getSubcategoriesForCategory,
  normalizeLegacySubcategorySlug,
} from '../../util/storeTaxonomy';
import Decimal from 'decimal.js';

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 42 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
const RESULT_PAGE_SIZE = 42;

// Type handlers for deserializing Transit responses
const typeHandlers = [
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

// ================ Action types ================ //

export const FETCH_LISTINGS_REQUEST = 'app/ManageListingsPage/FETCH_LISTINGS_REQUEST';
export const FETCH_LISTINGS_SUCCESS = 'app/ManageListingsPage/FETCH_LISTINGS_SUCCESS';
export const FETCH_LISTINGS_ERROR = 'app/ManageListingsPage/FETCH_LISTINGS_ERROR';

export const OPEN_LISTING_REQUEST = 'app/ManageListingsPage/OPEN_LISTING_REQUEST';
export const OPEN_LISTING_SUCCESS = 'app/ManageListingsPage/OPEN_LISTING_SUCCESS';
export const OPEN_LISTING_ERROR = 'app/ManageListingsPage/OPEN_LISTING_ERROR';

export const CLOSE_LISTING_REQUEST = 'app/ManageListingsPage/CLOSE_LISTING_REQUEST';
export const CLOSE_LISTING_SUCCESS = 'app/ManageListingsPage/CLOSE_LISTING_SUCCESS';
export const CLOSE_LISTING_ERROR = 'app/ManageListingsPage/CLOSE_LISTING_ERROR';

export const PUBLISH_LISTING_REQUEST = 'app/ManageListingsPage/PUBLISH_LISTING_REQUEST';
export const PUBLISH_LISTING_SUCCESS = 'app/ManageListingsPage/PUBLISH_LISTING_SUCCESS';
export const PUBLISH_LISTING_ERROR = 'app/ManageListingsPage/PUBLISH_LISTING_ERROR';

export const DELETE_LISTING_REQUEST = 'app/ManageListingsPage/DELETE_LISTING_REQUEST';
export const DELETE_LISTING_SUCCESS = 'app/ManageListingsPage/DELETE_LISTING_SUCCESS';
export const DELETE_LISTING_ERROR = 'app/ManageListingsPage/DELETE_LISTING_ERROR';

export const ADD_OWN_ENTITIES = 'app/ManageListingsPage/ADD_OWN_ENTITIES';

export const SET_CATEGORIES = 'app/ManageListingsPage/SET_CATEGORIES';
export const SET_SUBCATEGORIES = 'app/ManageListingsPage/SET_SUBCATEGORIES';
export const SET_CURRENT_SUBCATEGORIES = 'app/ManageListingsPage/SET_CURRENT_SUBCATEGORIES';
export const SET_SELECTED_CATEGORY = 'app/ManageListingsPage/SET_SELECTED_CATEGORY';
export const SET_CURRENT_ATTRIBUTES = 'app/ManageListingsPage/SET_CURRENT_ATTRIBUTES';

export const SET_SELECTED_CREATOR = 'app/ManageListingsPage/SET_SELECTED_CREATOR';

export const SET_FILTERED = 'app/ManageListingsPage/SET_FILTERED';
export const SET_SELECTED_IDS = 'app/ManageListingsPage/SET_SELECTED_IDS';

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  queryParams: null,
  queryInProgress: false,
  queryListingsError: null,
  currentPageResultIds: [],
  ownEntities: {},
  openingListing: null,
  openingListingError: null,
  closingListing: null,
  closingListingError: null,
  publishingListing: null,
  publishingListingError: null,
  deletingListing: null,
  deletingListingError: null,
  categories: [],
  selectedCategory: null,
  selectedCreator: null,
  subcategories: [],
  currentSubcategories: [],
  currentAttributes: ['draft', 'live', 'sold_out'],
  filtered: false,
  selectedIds: [],
};

const resultIds = data => data.data.map(l => l.id);

const merge = (state, sdkResponse) => {
  const apiResponse = sdkResponse.data;
  return {
    ...state,
    ownEntities: updatedEntities({ ...state.ownEntities }, apiResponse),
  };
};

const updateListingAttributes = (state, listingEntity) => {
  const oldListing = state.ownEntities.ownListing[listingEntity.id.uuid];
  const updatedListing = { ...oldListing, attributes: listingEntity.attributes };
  const ownListingEntities = {
    ...state.ownEntities.ownListing,
    [listingEntity.id.uuid]: updatedListing,
  };
  return {
    ...state,
    ownEntities: { ...state.ownEntities, ownListing: ownListingEntities },
  };
};

const manageListingsPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case FETCH_LISTINGS_REQUEST:
      return {
        ...state,
        queryParams: payload.queryParams,
        queryInProgress: true,
        queryListingsError: null,
        currentPageResultIds: [],
      };
    case FETCH_LISTINGS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        queryInProgress: false,
      };
    case FETCH_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, queryInProgress: false, queryListingsError: payload };

    case OPEN_LISTING_REQUEST:
      return {
        ...state,
        openingListing: payload.listingId,
        openingListingError: null,
      };
    case OPEN_LISTING_SUCCESS:
      return {
        ...updateListingAttributes(state, payload.data),
        openingListing: null,
      };
    case OPEN_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        openingListing: null,
        openingListingError: {
          listingId: state.openingListing,
          error: payload,
        },
      };
    }

    case CLOSE_LISTING_REQUEST:
      return {
        ...state,
        closingListing: payload.listingId,
        closingListingError: null,
      };
    case CLOSE_LISTING_SUCCESS: {
      const closedListingId = payload.data.id.uuid;
      const remainingResultIds = state.currentPageResultIds.filter(
        id => id.uuid !== closedListingId
      );
      const remainingTotalItems = Math.max((state.pagination?.totalItems || 1) - 1, 0);
      return {
        ...updateListingAttributes(state, payload.data),
        currentPageResultIds: remainingResultIds,
        pagination: state.pagination
          ? {
              ...state.pagination,
              totalItems: remainingTotalItems,
              totalPages: Math.max(
                Math.ceil(remainingTotalItems / state.pagination.perPage),
                remainingTotalItems ? 1 : 0
              ),
            }
          : state.pagination,
        closingListing: null,
      };
    }
    case CLOSE_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        closingListing: null,
        closingListingError: {
          listingId: state.closingListing,
          error: payload,
        },
      };
    }

    case PUBLISH_LISTING_REQUEST:
      return {
        ...state,
        publishingListing: payload.listingId,
        publishingListingError: null,
      };
    case PUBLISH_LISTING_SUCCESS:
      return {
        ...updateListingAttributes(state, payload.data),
        publishingListing: null,
      };
    case PUBLISH_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        publishingListing: null,
        publishingListingError: {
          listingId: state.publishingListing,
          error: payload,
        },
      };
    }

    case DELETE_LISTING_REQUEST:
      return {
        ...state,
        deletingListing: payload.listingId,
        deletingListingError: null,
      };
    case DELETE_LISTING_SUCCESS: {
      const deletedListingId = state.deletingListing.uuid;
      // Remove the deleted listing from currentPageResultIds
      const updatedResultIds = state.currentPageResultIds.filter(id => id.uuid !== deletedListingId);
      // Remove the deleted listing from ownEntities
      const { [deletedListingId]: removed, ...remainingListings } = state.ownEntities.ownListing || {};

      return {
        ...state,
        currentPageResultIds: updatedResultIds,
        ownEntities: {
          ...state.ownEntities,
          ownListing: remainingListings,
        },
        deletingListing: null,
      };
    }
    case DELETE_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        deletingListing: null,
        deletingListingError: {
          listingId: state.deletingListing,
          error: payload,
        },
      };
    }

    case ADD_OWN_ENTITIES:
      return merge(state, payload);

    case SET_CATEGORIES:
      return { ...state, categories: payload };

    case SET_SUBCATEGORIES:
      return { ...state, subcategories: payload };

    case SET_SELECTED_CATEGORY:
      return { ...state, selectedCategory: payload };

    case SET_SELECTED_CREATOR:
      return { ...state, selectedCreator: payload };

    case SET_CURRENT_ATTRIBUTES:
      return { ...state, currentAttributes: payload };

    case SET_CURRENT_SUBCATEGORIES:
      return { ...state, currentSubcategories: payload };

    case SET_FILTERED:
      return { ...state, filtered: payload };

    case SET_SELECTED_IDS:
      return { ...state, selectedIds: payload };

    default:
      return state;
  }
};

export default manageListingsPageReducer;

// ================ Selectors ================ //

/**
 * Get the denormalised own listing entities with the given IDs
 *
 * @param {Object} state the full Redux store
 * @param {Array<UUID>} listingIds listing IDs to select from the store
 */
export const getOwnListingsById = (state, listingIds) => {
  const { ownEntities } = state.ManageListingsPage;
  const resources = listingIds.map(id => ({
    id,
    type: 'ownListing',
  }));

  const throwIfNotFound = false;
  return denormalisedEntities(ownEntities, resources, throwIfNotFound);
};

// ================ Action creators ================ //

// This works the same way as addMarketplaceEntities,
// but we don't want to mix own listings with searched listings
// (own listings data contains different info - e.g. exact location etc.)
export const addOwnEntities = sdkResponse => ({
  type: ADD_OWN_ENTITIES,
  payload: sdkResponse,
});

export const openListingRequest = listingId => ({
  type: OPEN_LISTING_REQUEST,
  payload: { listingId },
});

export const openListingSuccess = response => ({
  type: OPEN_LISTING_SUCCESS,
  payload: response.data,
});

export const openListingError = e => ({
  type: OPEN_LISTING_ERROR,
  error: true,
  payload: e,
});

export const closeListingRequest = listingId => ({
  type: CLOSE_LISTING_REQUEST,
  payload: { listingId },
});

export const closeListingSuccess = response => ({
  type: CLOSE_LISTING_SUCCESS,
  payload: response.data,
});

export const closeListingError = e => ({
  type: CLOSE_LISTING_ERROR,
  error: true,
  payload: e,
});

export const publishListingRequest = listingId => ({
  type: PUBLISH_LISTING_REQUEST,
  payload: { listingId },
});

export const publishListingSuccess = response => ({
  type: PUBLISH_LISTING_SUCCESS,
  payload: response.data,
});

export const publishListingError = e => ({
  type: PUBLISH_LISTING_ERROR,
  error: true,
  payload: e,
});

export const deleteListingRequest = listingId => ({
  type: DELETE_LISTING_REQUEST,
  payload: { listingId },
});

export const deleteListingSuccess = response => ({
  type: DELETE_LISTING_SUCCESS,
  payload: response.data,
});

export const deleteListingError = e => ({
  type: DELETE_LISTING_ERROR,
  error: true,
  payload: e,
});

export const queryListingsRequest = queryParams => ({
  type: FETCH_LISTINGS_REQUEST,
  payload: { queryParams },
});

export const queryListingsSuccess = response => ({
  type: FETCH_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const queryListingsError = e => ({
  type: FETCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const setCategories = e => ({
  type: SET_CATEGORIES,
  payload: e,
});

export const setSubcategories = e => ({
  type: SET_SUBCATEGORIES,
  payload: e,
});

export const setCurrentCategory = e => ({
  type: SET_SELECTED_CATEGORY,
  payload: e,
});

export const setCurrentCreator = e => ({
  type: SET_SELECTED_CREATOR,
  payload: e,
});

export const setCurrentAttributes = e => ({
  type: SET_CURRENT_ATTRIBUTES,
  payload: e,
});

export const setCurrentSubcategories = e => ({
  type: SET_CURRENT_SUBCATEGORIES,
  payload: e,
});

export const setFiltered = e => ({
  type: SET_FILTERED,
  payload: e,
});

export const setSelectedIds = e => ({
  type: SET_SELECTED_IDS,
  payload: e,
});

const listingEntityKey = entity => `${entity.type}:${entity.id.uuid}`;

export const mergeOwnListingSearchResponses = (responses, page, perPage) => {
  const listingsById = new Map();
  const includedById = new Map();

  responses.filter(Boolean).forEach(response => {
    (response?.data?.data || []).forEach(listing =>
      listingsById.set(listing.id.uuid, { ...listing, type: 'ownListing' })
    );
    (response?.data?.included || []).forEach(entity =>
      includedById.set(listingEntityKey(entity), entity)
    );
  });

  const listings = Array.from(listingsById.values()).sort(
    (a, b) => new Date(b.attributes.createdAt) - new Date(a.attributes.createdAt)
  );
  const totalItems = listings.length;
  const totalPages = Math.ceil(totalItems / perPage);
  const safePage = Math.min(Math.max(Number(page) || 1, 1), Math.max(totalPages, 1));

  return {
    data: {
      data: listings.slice((safePage - 1) * perPage, safePage * perPage),
      included: Array.from(includedById.values()),
      meta: { page: safePage, perPage, totalItems, totalPages },
    },
  };
};

const fetchAllOwnListingPages = async fetchPage => {
  const firstResponse = await fetchPage(1);
  const totalPages = firstResponse?.data?.meta?.totalPages || 1;
  const remainingResponses = await Promise.all(
    Array.from({ length: Math.max(totalPages - 1, 0) }, (_, index) => fetchPage(index + 2))
  );
  return [firstResponse, ...remainingResponses];
};

// Throwing error for new (loadData may need that info)
export const queryOwnListings = (queryParams, config) => async (dispatch, getState, sdk) => {
  dispatch(queryListingsRequest(queryParams));

  let state = getState();
  let currentUser = state?.user?.currentUser;
  if (!currentUser) {
    let currentUserResponse = await sdk.currentUser.show();
    currentUser = currentUserResponse?.data?.data;
  }

  let [team] = currentUser?.attributes?.profile?.publicData?.teams || [];

  const { perPage, ...rest } = queryParams;
  const params = { ...rest, perPage };

  dispatch(setCategories(STORE_SIDEBAR_CATEGORIES));
  dispatch(setSubcategories(STORE_SIDEBAR_SUBCATEGORIES));

  const callbackFn = response => {
    dispatch(addOwnEntities(response));
    dispatch(queryListingsSuccess(response));
  };

  if (team && params.keywords) {
    const requestedPage = params.page || 1;
    const requestedPerPage = params.perPage || RESULT_PAGE_SIZE;
    const completeSearchParams = { ...params, page: 1, perPage: 100 };

    return Promise.all([
      fetchAllOwnListingPages(page =>
        sdk.ownListings.query({ ...completeSearchParams, page })
      ),
      fetchAllOwnListingPages(page =>
        getTeamAssociatedListing({
          teamId: team.id,
          memberId: currentUser.id,
          params: { ...completeSearchParams, page },
        })
      ),
    ])
      .then(([authorResponses, teamResponses]) => {
        const response = mergeOwnListingSearchResponses(
          [...teamResponses, ...authorResponses],
          requestedPage,
          requestedPerPage
        );
        callbackFn(response);
        return response;
      })
      .catch(e => {
        dispatch(queryListingsError(storableError(e)));
        throw e;
      });
  }

  return sdk.ownListings
    .query(params)
    .then(response => {
      if (team) {
        getTeamAssociatedListing({ teamId: team.id, memberId: currentUser.id, params })
          .then(res => {
            let authorListings = response?.data?.data || [];
            let creatorListings = res?.data?.data || [];

            let authorIncluded = response?.data?.included || [];
            let creatorIncluded = res?.data?.included || [];

            response.data.data = [...creatorListings, ...authorListings];
            response.data.included = [...creatorIncluded, ...authorIncluded];
            callbackFn(response);
          })
          .catch(() => {
            callbackFn(response);
          });
      } else {
        callbackFn(response);
      }
      return response;
    })
    .catch(e => {
      dispatch(queryListingsError(storableError(e)));
      throw e;
    });
};

export const closeListing = listingId => (dispatch, getState, sdk) => {
  dispatch(closeListingRequest(listingId));

  return sdk.ownListings
    .close({ id: listingId }, { expand: true })
    .then(response => {
      dispatch(closeListingSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(closeListingError(storableError(e)));
    });
};

export const openListing = listingId => (dispatch, getState, sdk) => {
  dispatch(openListingRequest(listingId));

  return sdk.ownListings
    .open({ id: listingId }, { expand: true })
    .then(response => {
      dispatch(openListingSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(openListingError(storableError(e)));
    });
};

export const publishListing = listingId => (dispatch, getState, sdk) => {
  dispatch(publishListingRequest(listingId));

  // Extract UUID string from the listingId object
  const listingIdString = typeof listingId === 'object' ? listingId.uuid : listingId;

  const bodyParams = {
    listingId: listingIdString,
  };

  const apiUrl = `${apiBaseUrl()}/api/approve-listing`;

  return fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(bodyParams),
    credentials: 'include', // Important: include cookies for authentication
  })
    .then(res => {
      if (!res.ok) {
        return res.json().then(err => {
          throw new Error(err.error || 'Failed to approve listing');
        });
      }

      // Check if response is Transit format
      const contentType = res.headers.get('Content-Type');
      if (contentType && contentType.includes('application/transit+json')) {
        return res.text().then(text => transit.read(text, { typeHandlers }));
      }

      return res.json();
    })
    .then(data => {
      // Transform response to match SDK format
      const response = { data };
      dispatch(publishListingSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(publishListingError(storableError(e)));
    });
};

export const deleteListing = listingId => (dispatch, getState, sdk) => {
  dispatch(deleteListingRequest(listingId));

  // Extract UUID string from the listingId object
  const listingIdString = typeof listingId === 'object' ? listingId.uuid : listingId;

  const bodyParams = {
    listingId: listingIdString,
  };

  const apiUrl = `${apiBaseUrl()}/api/delete-listing`;

  return fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(bodyParams),
    credentials: 'include', // Important: include cookies for authentication
  })
    .then(res => {
      if (!res.ok) {
        return res.json().then(err => {
          throw new Error(err.error || 'Failed to delete listing');
        });
      }
      return res.json();
    })
    .then(data => {
      // Transform response to match SDK format
      const response = { data };
      dispatch(deleteListingSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(deleteListingError(storableError(e)));
    });
};

export const loadData = (params, search, config) => {
  const queryParams = parse(search);
  const page = queryParams.page || 1;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  return queryOwnListings(
    {
      ...queryParams,
      keywords: queryParams.keywords ? String(queryParams.keywords).trim() : undefined,
      states: ['draft', 'pendingApproval', 'published'],
      page,
      perPage: RESULT_PAGE_SIZE,
      include: ['images', 'currentStock'],
      'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      'limit.images': 1,
    },
    config
  );
};

export const getListings = (params, listings) => (dispatch, getState, sdk) => {
  let filtered = false;
  let items = [];

  const { pub_category, pub_subcategory, pub_creatorId } = params;

  if (pub_category !== undefined && pub_category != null) {
    filtered = true;
    listings = listings.filter(l => l.attributes?.publicData?.category == pub_category);
  }

  if (
    pub_category !== undefined &&
    pub_category != null &&
    pub_subcategory !== undefined &&
    pub_subcategory !== null
  ) {
    filtered = true;
    const normalizedSelectedSubcategory = normalizeLegacySubcategorySlug(pub_subcategory);
    listings = listings.filter(l => {
      const listingSubcategory = l.attributes?.publicData?.subcategory;
      return normalizeLegacySubcategorySlug(listingSubcategory) == normalizedSelectedSubcategory;
    });
  }

  let attrs = params['listings-attrs'] || null;

  if (attrs && !attrs.includes('draft')) {
    filtered = true;
    listings = listings.filter(l => l.attributes?.state != 'draft');
  }

  if (attrs && !attrs.includes('live')) {
    filtered = true;
    listings = listings.filter(l => {
      let stock = l.currentStock?.attributes?.quantity || 0;
      return l.attributes?.state != 'published' || !stock;
    });
  }

  if (attrs && !attrs.includes('sold_out')) {
    filtered = true;
    listings = listings.filter(l => {
      let stock = l.currentStock?.attributes?.quantity || 0;
      return !!stock;
      // let state = l.attributes?.state || 'draft';
      // return (state == 'published' && stock) || (!['draft', 'closed'].includes(state) && state != 'published');
    });
  }

  if (pub_creatorId !== undefined) {
    filtered = true;
    listings = listings.filter(l => l.attributes?.publicData?.creatorId == pub_creatorId);
  }

  if (filtered) {
    items = listings.map(l => l.id.uuid);
  }
  dispatch(setFiltered(filtered));
  dispatch(setSelectedIds(items));
};

export const changePropsCategory = values => (dispatch, getState, sdk) => {
  let currentSubcategories = [];
  let category = values.pub_category;
  if (category) {
    currentSubcategories = getSubcategoriesForCategory(category);
  }
  dispatch(setCurrentAttributes(values['listings-attrs']));
  dispatch(setCurrentCategory(category));
  dispatch(setCurrentSubcategories(currentSubcategories));
};

export const changeTeamPropsCategory = values => (dispatch, getState, sdk) => {
  let creator = values.pub_creatorId;
  dispatch(setCurrentCreator(creator));
};
